import { supabase } from '@/lib/supabaseClient';
import type {
  AuditReport,
  AuditScoreResult,
  CandidatePlace,
  ComparisonCategory,
  ComparisonCategoryMode,
  ComparisonCategorySource,
  GmbAuditReport,
  PeerSearchResult,
  PeerSelectionMode,
  PlaceSnapshot,
  SampleConfidence,
} from '@/types/gmb';

// 前端 → Readdy Backend Edge Functions 串接層。
// Batch 3：search_google_places 已正式串接 Google Places Text Search (New)，
// 會回傳真實候選商家（僅 placeId / displayName / formattedAddress / primaryType）。
//
// 統一 Response Envelope：
//   { ok, data, error: { code, message } | null, meta: { requestId } }

interface ApiErrorPayload {
  code: string;
  message: string;
}

interface ApiEnvelope<T> {
  ok: boolean;
  data: T | null;
  error: ApiErrorPayload | null;
  meta: { requestId: string };
}

interface SearchPlaceCandidate {
  placeId: string;
  displayName: string;
  formattedAddress?: string;
  secondaryText?: string;
  primaryType?: string;
  pureServiceAreaBusiness?: boolean;
}

/** 搜尋結果：成功帶候選清單，失敗帶後端 error code 供 UI 分流。 */
export type SearchPlacesResult =
  | { ok: true; places: CandidatePlace[] }
  | { ok: false; code: string };

const NETWORK_FALLBACK_CODE = 'INTERNAL_ERROR';

async function invokeFunction<T>(name: string, body?: Record<string, unknown>): Promise<ApiEnvelope<T>> {
  const fallback: ApiEnvelope<T> = {
    ok: false,
    data: null,
    error: { code: NETWORK_FALLBACK_CODE, message: '服務暫時無法使用，請稍後再試。' },
    meta: { requestId: '' },
  };

  try {
    const { data, error } = await supabase.functions.invoke(name, body ? { body } : undefined);
    if (error) {
      // 非 2xx（例如 429 RATE_LIMITED / 503 RATE_LIMIT_UNAVAILABLE / 405 / 413）：
      // Backend 仍回傳統一 envelope，必須從 response body 取回 error.code / message，
      // 否則前端一律顯示通用錯誤，將看不到 RATE_LIMITED 等具體狀態。
      const ctx = (error as { context?: unknown }).context;
      if (ctx && typeof (ctx as Response).json === 'function') {
        try {
          const parsed = await (ctx as Response).clone().json();
          if (parsed && typeof parsed === 'object') {
            return parsed as ApiEnvelope<T>;
          }
        } catch {
          // 無法解析 → 使用 fallback
        }
      }
      return fallback;
    }
    if (!data) {
      return fallback;
    }
    return data as ApiEnvelope<T>;
  } catch {
    return fallback;
  }
}

/**
 * 呼叫 search_google_places（Google Text Search (New)，並於需要時補一次 Autocomplete (New)）。
 * 後端會將「店家名稱 + 城市」組成 textQuery；有城市 pageSize 10、無城市 pageSize 20，
 * 無城市且低可信度時會再以 Autocomplete 補召回，最後以 placeId 去重並 rerank。
 * 前端只取回最多 5 筆候選。無結果回傳空陣列，錯誤回傳 error code，絕不產生假資料。
 */
export async function searchCandidatePlaces(name: string, region: string): Promise<SearchPlacesResult> {
  const res = await invokeFunction<{ places: SearchPlaceCandidate[] }>('search_google_places', {
    query: name.trim(),
    city: region.trim() || undefined,
  });

  if (!res.ok || !res.data) {
    return { ok: false, code: res.error?.code ?? NETWORK_FALLBACK_CODE };
  }

  const places = Array.isArray(res.data.places) ? res.data.places : [];
  // 前端永遠只顯示 Top 5；不因缺少 formattedAddress 而過濾服務區域商家。
  return {
    ok: true,
    places: places.slice(0, 5).map((p) => ({
      placeId: p.placeId,
      name: p.displayName,
      address: p.formattedAddress ?? '',
      secondaryText: p.secondaryText,
      primaryCategory: p.primaryType ?? '',
      pureServiceAreaBusiness: p.pureServiceAreaBusiness,
    })),
  };
}

/** 取得商家快照結果：成功帶 normalized Snapshot，失敗帶後端 error code 與訊息供 UI 顯示。 */
export type PlaceSnapshotResult =
  | { ok: true; snapshot: PlaceSnapshot }
  | { ok: false; code: string; message: string };

/**
 * 呼叫 get_google_place_snapshot（Google Place Details (New)）。
 * 後端以 selectedCandidate.placeId 向 Google 取得公開資料並映射為 PlaceSnapshot；
 * 不回傳任何 Google raw response。Service Area Business 缺地址／缺座標仍算成功。
 */
export async function fetchPlaceSnapshot(placeId: string): Promise<PlaceSnapshotResult> {
  const res = await invokeFunction<PlaceSnapshot>('get_google_place_snapshot', { placeId });

  if (!res.ok || !res.data) {
    return {
      ok: false,
      code: res.error?.code ?? NETWORK_FALLBACK_CODE,
      message: res.error?.message ?? '服務暫時無法使用，請稍後再試。',
    };
  }

  return { ok: true, snapshot: res.data };
}

/** 同業搜尋請求（Batch 5-D2：新增 comparisonCategoryMode，支援 USER_CONFIRMED keyword 路徑）。 */
export interface PeerSearchRequest {
  placeId: string;
  latitude?: number;
  longitude?: number;
  primaryType?: string;
  primaryTypeDisplayName?: string;
  /** Google 回傳的所有商家類型（供後端決策層判斷） */
  types?: string[];
  /** 前端已解析的比較類型（GOOGLE_PRIMARY_TYPE / GOOGLE_TYPES_FALLBACK） */
  comparisonType?: string;
  /** 使用者確認的同業分類關鍵字（USER_CONFIRMED → Keyword Text Search） */
  comparisonKeyword?: string;
  /** 前端決策層解析出的分類來源（GOOGLE_PRIMARY_TYPE / GOOGLE_TYPES_FALLBACK） */
  comparisonCategoryMode?: ComparisonCategoryMode;
  pureServiceAreaBusiness?: boolean;
  serviceAreaHint?: string;
}

/** 同業搜尋結果：成功帶 mode / 統計 / peers，失敗帶後端 error code 與訊息。 */
export type PeerSearchResultApi =
  | {
      ok: true;
      mode: string;
      selectionMode: PeerSelectionMode | null;
      comparisonCategoryLabel: string | null;
      comparisonCategorySource: ComparisonCategorySource | null;
      radiusUsedM: number | null;
      comparisonArea: string | null;
      sampleCount: number;
      sampleConfidence: SampleConfidence;
      ratingSampleCount: number;
      reviewCountSampleCount: number;
      ratingMedian: number | null;
      reviewCountMedian: number | null;
      peers: PeerSearchResult['peers'];
    }
  | { ok: false; code: string; message: string };

/**
 * 呼叫 find_google_place_peers。
 * Path A（一般實體商家）：Nearby Search (New)，1500m→3000m→5000m→10000m，Haversine 距離。
 * Path B（Service Area Business）：Text Search (New) + serviceAreaHint，回 mode = SERVICE_AREA。
 * 統一回傳 mode / radiusUsedM / comparisonArea / peers。
 */
export async function fetchPeers(req: PeerSearchRequest): Promise<PeerSearchResultApi> {
  const res = await invokeFunction<PeerSearchResult>('find_google_place_peers', { ...req });

  if (!res.ok || !res.data) {
    return {
      ok: false,
      code: res.error?.code ?? NETWORK_FALLBACK_CODE,
      message: res.error?.message ?? '服務暫時無法使用，請稍後再試。',
    };
  }

  return {
    ok: true,
    mode: res.data.mode,
    selectionMode: res.data.selectionMode ?? null,
    comparisonCategoryLabel:
      typeof res.data.comparisonCategoryLabel === 'string' ? res.data.comparisonCategoryLabel : null,
    comparisonCategorySource: res.data.comparisonCategorySource ?? null,
    radiusUsedM: typeof res.data.radiusUsedM === 'number' ? res.data.radiusUsedM : null,
    comparisonArea: typeof res.data.comparisonArea === 'string' ? res.data.comparisonArea : null,
    sampleCount: typeof res.data.sampleCount === 'number' ? res.data.sampleCount : 0,
    sampleConfidence: res.data.sampleConfidence ?? 'NONE',
    ratingSampleCount: typeof res.data.ratingSampleCount === 'number' ? res.data.ratingSampleCount : 0,
    reviewCountSampleCount:
      typeof res.data.reviewCountSampleCount === 'number' ? res.data.reviewCountSampleCount : 0,
    ratingMedian: typeof res.data.ratingMedian === 'number' ? res.data.ratingMedian : null,
    reviewCountMedian:
      typeof res.data.reviewCountMedian === 'number' ? res.data.reviewCountMedian : null,
    peers: Array.isArray(res.data.peers) ? res.data.peers : [],
  };
}

/** 計分請求：直接把目前 runtime 已取得的資料送進 Backend，不重新呼叫任何 Google API。 */
export interface CalculateAuditScoreRequest {
  targetSnapshot: PlaceSnapshot;
  comparisonCategory: ComparisonCategory;
  /** 同業統計單一來源（Backend 優先且只使用 peerComparison） */
  peerComparison: {
    sampleCount: number;
    ratingSampleCount: number;
    reviewCountSampleCount: number;
    ratingMedian?: number;
    reviewCountMedian?: number;
  };
}

/** 計分結果：成功帶 AuditScoreResult，失敗帶後端 error code 與訊息。 */
export type CalculateAuditScoreApi =
  | { ok: true; result: AuditScoreResult }
  | { ok: false; code: string; message: string };

/**
 * 呼叫 calculate_gmb_audit_score（Backend 為 Scoring 唯一來源）。
 * 前端不複製 11 項公式、Grade、Coverage、Priority；只負責把既有資料送出並顯示結果。
 * Backend 不呼叫任何 Google API、不讀 Secret、完全 deterministic。
 */
export async function calculateAuditScore(
  req: CalculateAuditScoreRequest,
): Promise<CalculateAuditScoreApi> {
  const res = await invokeFunction<AuditScoreResult>('calculate_gmb_audit_score', {
    targetSnapshot: req.targetSnapshot,
    comparisonCategory: req.comparisonCategory,
    peerComparison: {
      sampleCount: req.peerComparison.sampleCount,
      ratingSampleCount: req.peerComparison.ratingSampleCount,
      reviewCountSampleCount: req.peerComparison.reviewCountSampleCount,
      ratingMedian: req.peerComparison.ratingMedian,
      reviewCountMedian: req.peerComparison.reviewCountMedian,
    },
  });

  if (!res.ok || !res.data) {
    return {
      ok: false,
      code: res.error?.code ?? NETWORK_FALLBACK_CODE,
      message: res.error?.message ?? '服務暫時無法使用，請稍後再試。',
    };
  }

  return { ok: true, result: res.data };
}

/** 呼叫 get_gmb_audit_result（未來：取得健診結果）。目前後端尚無結果，回傳 null。 */
export async function fetchAuditReport(auditId: string): Promise<AuditReport | null> {
  const res = await invokeFunction<AuditReport>(`get_gmb_audit_result?auditId=${encodeURIComponent(auditId)}`);
  if (!res.ok || !res.data) {
    return null;
  }
  // TODO(backend batch): 後端完成後，需將後端 DTO 映射為前端 AuditReport 結構。
  return null;
}

// ---------------------------------------------------------------------------
// Batch 7-B2B｜歷史 Snapshot 讀取（get_gmb_audit_result）
// 只以精確 reportToken 讀取已保存的 Audit；前端不得直接 supabase.from('gmb_audits')。
// ---------------------------------------------------------------------------

/** get_gmb_audit_result 回傳的歷史報告（report 直接等同前端 GmbAuditReport）。 */
export interface RetrievedGmbAuditReport {
  reportToken: string;
  createdAt: string;
  schemaVersion: number;
  scoringModelVersion: string;
  peerModelVersion: string;
  report: GmbAuditReport;
}

/** 讀取結果：成功帶歷史報告，失敗帶後端 error code 與訊息供 UI 分流。 */
export type GetGmbAuditResultApi =
  | { ok: true; data: RetrievedGmbAuditReport }
  | { ok: false; code: string; message: string };

/**
 * 呼叫 get_gmb_audit_result，以精確 reportToken 讀取已保存的歷史 Snapshot。
 * 後端完全由 Database 讀取，不重新呼叫 Google API、不重新計算任何分數。
 * 後端回傳的 data.report 已可直接視為前端 GmbAuditReport；peerComparison 為 null 時轉為 undefined。
 */
export async function getGmbAuditResult(reportToken: string): Promise<GetGmbAuditResultApi> {
  const res = await invokeFunction<RetrievedGmbAuditReport>('get_gmb_audit_result', { reportToken });

  if (!res.ok || !res.data) {
    return {
      ok: false,
      code: res.error?.code ?? NETWORK_FALLBACK_CODE,
      message: res.error?.message ?? '讀取報告失敗，請稍後再試。',
    };
  }

  const raw = res.data;
  const report: GmbAuditReport = {
    target: raw.report.target,
    comparisonCategory: raw.report.comparisonCategory,
    peerComparison: raw.report.peerComparison ?? undefined,
    scoreResult: raw.report.scoreResult,
  };

  return {
    ok: true,
    data: {
      reportToken: raw.reportToken,
      createdAt: raw.createdAt,
      schemaVersion: raw.schemaVersion,
      scoringModelVersion: raw.scoringModelVersion,
      peerModelVersion: raw.peerModelVersion,
      report,
    },
  };
}

// ---------------------------------------------------------------------------
// Batch 7-B2A｜手動保存健診報告（save_gmb_audit）
// 前端只呼叫 Backend Function，絕對不直接 supabase.from('gmb_audits') 等四表做任何讀寫。
// ---------------------------------------------------------------------------

/** save_gmb_audit 請求：只送 runtime 已有的四份資料 + saveRequestId（其餘系統欄位由 Backend/DB 決定）。 */
export interface SaveGmbAuditRequest {
  targetSnapshot: PlaceSnapshot;
  comparisonCategory: ComparisonCategory;
  peerComparison: PeerSearchResult | null;
  scoreResult: AuditScoreResult;
  /** 同一份 AuditReport 沿用同一個 UUID；新報告才產生新的 UUID。 */
  saveRequestId: string;
  /** Batch 7-C：匿名 History Scope（同一 browser + 同一 placeId 沿用同一個 UUID）。 */
  historyScopeToken: string;
}

/** save_gmb_audit 成功回應（reused = true 表示命中既有報告、未重複建立）。 */
export interface SaveGmbAuditData {
  reportToken: string;
  createdAt: string;
  reused: boolean;
}

/** 保存結果：成功帶 reportToken/createdAt/reused，失敗帶後端 error code 與訊息供 UI 分流。 */
export type SaveGmbAuditApi =
  | { ok: true; data: SaveGmbAuditData }
  | { ok: false; code: string; message: string };

/**
 * 呼叫 save_gmb_audit（受控 persistence）。
 * Backend 使用 server-only privileged client 寫入四張 gmb_* 表；前端不接觸任何 DB credential。
 * 同一份報告重試沿用同一 saveRequestId，Backend 會回 reused:true 且 reportToken 不變。
 */
export async function saveGmbAudit(req: SaveGmbAuditRequest): Promise<SaveGmbAuditApi> {
  const res = await invokeFunction<SaveGmbAuditData>('save_gmb_audit', {
    targetSnapshot: req.targetSnapshot,
    comparisonCategory: req.comparisonCategory,
    peerComparison: req.peerComparison,
    scoreResult: req.scoreResult,
    saveRequestId: req.saveRequestId,
    historyScopeToken: req.historyScopeToken,
  });

  if (!res.ok || !res.data) {
    return {
      ok: false,
      code: res.error?.code ?? NETWORK_FALLBACK_CODE,
      message: res.error?.message ?? '報告保存失敗，請重新嘗試。',
    };
  }

  return { ok: true, data: res.data };
}

// ---------------------------------------------------------------------------
// Batch 7-C｜歷史健診紀錄（get_gmb_audit_history）
// 只以「匿名 historyScopeToken + targetPlaceId」列 Summary；前端不得直接 supabase.from('gmb_audits')。
// ---------------------------------------------------------------------------

/** 單筆歷史紀錄（Summary only；不含任何完整 Snapshot）。 */
export interface GmbAuditHistoryItem {
  reportToken: string;
  createdAt: string;
  schemaVersion: number;
  scoringModelVersion: string;
  peerModelVersion: string;
  score: number | null;
  scoreMode: string;
  healthGrade: string | null;
  healthLabel: string | null;
  dataCoveragePercent: number | null;
}

/** get_gmb_audit_history 回傳資料。 */
export interface GmbAuditHistoryData {
  targetPlaceId: string;
  targetDisplayName: string | null;
  audits: GmbAuditHistoryItem[];
  /** 超過單次回傳上限（20）時為 true。 */
  truncated: boolean;
}

/** 歷史紀錄讀取結果：成功帶 summary list，失敗帶後端 error code 與訊息。 */
export type GetGmbAuditHistoryApi =
  | { ok: true; data: GmbAuditHistoryData }
  | { ok: false; code: string; message: string };

/**
 * 呼叫 get_gmb_audit_history，列出同一 browser（同一 historyScopeToken）針對同一 placeId 的歷史紀錄。
 * 後端完全由 Database 讀取（created_at DESC），不重新呼叫 Google、Peer 或 Scoring。
 */
export async function getGmbAuditHistory(params: {
  historyScopeToken: string;
  targetPlaceId: string;
}): Promise<GetGmbAuditHistoryApi> {
  const res = await invokeFunction<GmbAuditHistoryData>('get_gmb_audit_history', {
    historyScopeToken: params.historyScopeToken,
    targetPlaceId: params.targetPlaceId,
  });

  if (!res.ok || !res.data) {
    return {
      ok: false,
      code: res.error?.code ?? NETWORK_FALLBACK_CODE,
      message: res.error?.message ?? '讀取歷史紀錄失敗，請稍後再試。',
    };
  }

  return { ok: true, data: res.data };
}